
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/use-toast';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { InfoIcon, AlertTriangleIcon, CheckCircleIcon } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { SolutionDetails } from '@/types/solution';

// Function to generate details based on solution name
const generateSolutionDetails = (
  name: string,
  shortDescription: string,
  category?: string
): SolutionDetails => {
  // Generate capabilities based on solution name
  const generateCapabilities = (solutionName: string) => {
    const commonCapabilities = [
      'API access to model functionalities',
      'Support for various input formats',
      'Comprehensive documentation'
    ];

    const specificCapabilities: Record<string, string[]> = {
      'ChatGPT': [
        'Advanced conversational abilities',
        'Code generation and interpretation',
        'Content creation and summarization',
        'Multiple model versions (3.5, 4o)',
        'Vision capabilities in latest models'
      ],
      'Claude': [
        'Long context window (up to 200K tokens)',
        'Advanced reasoning capabilities',
        'Document analysis and understanding',
        'Exceptional instruction following',
        'Vision capabilities in latest models'
      ],
      'Grok': [
        'Real-time information access',
        'Web browsing capabilities',
        'Quick responses with humor',
        'Argumentative and adversarial responses',
        'Fewer content restrictions than competitors'
      ],
      'Perplexity': [
        'Real-time web search integration',
        'Source citation for all information',
        'Query-focused response generation',
        'Follow-up question suggestions',
        'Pro features for deeper research'
      ],
      'Midjourney': [
        'Text-to-image generation',
        'Style and aesthetic control',
        'Aspect ratio and detail customization',
        'Image variations and mixing',
        'Advanced lighting and composition'
      ],
      'Kling': [
        'Text-to-video generation',
        'Realistic avatar rendering',
        'Natural voice synthesis',
        'Various avatar designs',
        'Customizable backgrounds and settings'
      ],
      'Luma AI': [
        'Text-to-video generation',
        'Video editing and enhancement',
        'Style transfer capabilities',
        'Professional-grade output quality',
        'Advanced motion control'
      ],
      'Sora': [
        'High-quality text-to-video generation',
        'Complex scene composition',
        'Natural physics simulation',
        'Temporal consistency across frames',
        'Precise prompt interpretation'
      ],
      'ElevenLabs': [
        'Ultra-realistic voice synthesis',
        'Multi-language support',
        'Voice cloning capabilities',
        'Emotion and tone control',
        'Speech-to-speech transformation'
      ]
    };

    // Return common capabilities plus any solution-specific ones
    return [...(specificCapabilities[solutionName] || []), ...commonCapabilities];
  };

  // Generate use cases based on solution name and category
  const generateUseCases = (solutionName: string, solutionCategory?: string) => {
    const commonUseCases = [
      'Research and education'
    ];

    // Category-specific use cases
    const categoryUseCases: Record<string, string[]> = {
      'Language': [
        'Content creation and editing',
        'Customer service automation',
        'Programming assistance',
        'Information retrieval and summarization'
      ],
      'Image': [
        'Creative design and artwork',
        'Product visualization',
        'Marketing and advertising material',
        'Concept exploration'
      ],
      'Video': [
        'Marketing video creation',
        'Educational content',
        'Social media content',
        'Product demonstrations'
      ],
      'Audio': [
        'Audiobook production',
        'Podcast creation',
        'Voice-over for videos',
        'Accessibility solutions'
      ],
      'Multimodal': [
        'Complex data analysis',
        'Visual and textual content creation',
        'Enhanced customer experiences',
        'Advanced research assistance'
      ]
    };

    // Solution-specific use cases
    const specificUseCases: Record<string, string[]> = {
      'ChatGPT': [
        'Writing assistance and editing',
        'Teaching and tutoring',
        'Personalized customer support'
      ],
      'Claude': [
        'Document analysis and summarization',
        'Complex reasoning tasks',
        'Content moderation'
      ],
      'Grok': [
        'Alternative viewpoint generation',
        'Debate preparation',
        'Creative brainstorming'
      ],
      'Perplexity': [
        'Research paper preparation',
        'Fact checking',
        'News summarization'
      ],
      'Midjourney': [
        'Book cover design',
        'Concept art creation',
        'Interior design visualization'
      ],
      'ElevenLabs': [
        'Localization of content',
        'Interactive fiction',
        'Personalized learning materials'
      ],
      'Sora': [
        'Short film creation',
        'Visual storytelling',
        'Special effects prototyping'
      ]
    };

    // Combine category-specific and solution-specific use cases
    const combinedUseCases = [
      ...(specificUseCases[solutionName] || []),
      ...(categoryUseCases[solutionCategory || ''] || []),
      ...commonUseCases
    ];

    // Return unique use cases (no duplicates)
    return Array.from(new Set(combinedUseCases));
  };

  // Generate detailed description
  const generateLongDescription = (solutionName: string, shortDescription: string) => {
    const detailedInfo: Record<string, string> = {
      'ChatGPT': `ChatGPT is a conversational AI system created by OpenAI that leverages large language models to generate human-like text based on the prompts it receives. 

Built on the GPT (Generative Pre-trained Transformer) architecture, ChatGPT can engage in dialogue, answer questions, create content, write code, and perform a wide range of language tasks with remarkable fluency and contextual understanding.

Recent versions of ChatGPT have expanded to include multimodal capabilities, allowing the system to process and generate content based on both text and images. The GPT-4o model represents the cutting edge of this technology, combining fast response times with advanced reasoning capabilities.

ChatGPT is available through both free and subscription-based plans, with the premium offering (ChatGPT Plus) providing access to the most advanced models, higher usage limits, and priority access during peak times.`,
      'Claude': `Claude is an AI assistant created by Anthropic, designed with a focus on helpfulness, harmlessness, and honesty. 

Claude excels at thoughtful dialogue and complex reasoning tasks, with a particularly strong ability to understand and follow detailed instructions. The assistant can analyze documents, draft content, explain complex topics, and assist with problem-solving across many domains.

Claude stands out for its nuanced understanding of context and its ability to work with very long prompts (up to 200,000 tokens in Claude 3 Opus), making it ideal for tasks involving large documents or extended conversations.

The latest Claude models include vision capabilities, allowing users to share images alongside text prompts for analysis, and the system is designed with robust safeguards to ensure responsible and appropriate responses.`,
      'Grok': `Grok is an AI assistant developed by xAI, designed to answer questions with wit, wisdom, and a touch of rebelliousness. 

Unlike some other AI systems, Grok is built to provide more candid and less filtered responses, making it well-suited for users seeking alternative viewpoints or more creative and unrestricted interactions.

Grok has real-time knowledge capabilities through web browsing, allowing it to respond to queries about current events and recent information not covered in its training data.

The assistant combines advanced reasoning abilities with a conversational style that incorporates humor and personality, creating a distinctive user experience that sets it apart in the AI assistant landscape.`,
      'Perplexity': `Perplexity is an AI-powered answer engine that combines the capabilities of search engines and large language models to provide comprehensive, up-to-date answers to user queries.

Unlike traditional search engines that return a list of links, Perplexity synthesizes information from across the web to generate direct, conversational responses while citing its sources, enabling users to quickly find and verify information.

Perplexity stands out for its ability to engage in follow-up questions, maintaining context throughout a conversation while continuously pulling in relevant information from the internet.

The platform offers both free access and a Pro subscription with enhanced features including access to more powerful AI models, higher usage limits, and additional customization options.`,
      'Midjourney': `Midjourney is an AI-powered text-to-image generation system known for its distinctively artistic and aesthetically pleasing outputs.

Users interact with Midjourney primarily through Discord, where they enter text prompts describing the image they want to create, and the AI generates corresponding visuals based on those descriptions.

Midjourney has become particularly popular among artists, designers, and creative professionals for its ability to produce visually striking images with a painterly quality that often resembles professional artwork.

The system offers extensive customization through a sophisticated prompting system, allowing users to specify artistic styles, composition elements, lighting conditions, and various technical parameters to achieve precise visual results.`,
      'Kling': `Kling is an AI video generation platform that transforms text inputs into engaging video content featuring realistic digital avatars and natural-sounding voices.

The platform enables users to create professional-looking videos without the need for cameras, microphones, or video editing expertise, making video production accessible to a broader audience.

Kling's technology combines several AI capabilities including text-to-speech, realistic avatar animation, and background generation to produce cohesive video presentations from simple text prompts.

The system is particularly useful for creating educational content, marketing materials, product demonstrations, and other video communications where a human presenter would traditionally be needed.`,
      'Luma AI': `Luma AI is a cutting-edge platform that leverages artificial intelligence to create and edit videos with sophisticated visual effects and enhancements.

The system offers tools for video generation, editing, and style transformation, enabling users to produce professional-quality video content with minimal technical expertise or equipment.

Luma AI's technology is particularly noted for its ability to understand and maintain the semantic meaning of scenes while applying transformations, ensuring that edited videos maintain coherence and natural-looking results.

The platform serves a wide range of use cases from content creation for social media to more sophisticated applications in advertising, entertainment, and virtual production.`,
      'Sora': `Sora is OpenAI's text-to-video model that can create realistic and imaginative scenes from text instructions. 

The model demonstrates remarkable capabilities in generating high-quality video with realistic motion, complex scenes, and multiple characters while maintaining visual consistency and adhering closely to user prompts.

Sora can render detailed scenes, multiple characters, specific camera movements, and subjects in motion, with the ability to generate videos up to one minute in length that maintain consistency throughout.

While still in research preview and not yet publicly available, Sora represents a significant advancement in AI-generated video, with potential applications spanning entertainment, education, advertising, and creative expression.`,
      'ElevenLabs': `ElevenLabs is an AI voice technology company that specializes in producing ultra-realistic, natural-sounding synthetic voices for various applications.

The platform offers text-to-speech capabilities that surpass traditional robotic-sounding systems, creating vocal outputs that capture the nuances of human speech including emotion, emphasis, and natural pacing.

ElevenLabs provides voice cloning technology that can create a digital replica of a voice from just a few minutes of audio samples, enabling personalized voice applications while maintaining ethical guidelines.

The company's technology supports multiple languages and accents, making it valuable for global content creation, and is being used across industries including publishing (audiobooks), media (podcasts, videos), accessibility, and education.`
    };

    return detailedInfo[solutionName] || `${shortDescription}\n\nThis AI solution offers cutting-edge capabilities in its field, providing users with powerful tools to enhance productivity and creativity. The technology continues to evolve rapidly, with new features and improvements being added regularly to meet the growing demands of various use cases.`;
  };

  // Create the details object
  return {
    overview: generateLongDescription(name, shortDescription),
    capabilities: generateCapabilities(name),
    useCases: generateUseCases(name, category),
    limitations: [
      'Results may vary depending on input quality and specific use case',
      'May occasionally produce inaccurate or biased information',
      'Performance can be limited by the specific model version being used',
      'Usage may be subject to rate limits, especially with free tiers',
      'Costs can scale significantly with heavy usage'
    ]
  };
};

const MigrateSolutionData: React.FC = () => {
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState<'idle' | 'running' | 'completed' | 'error'>('idle');
  const [results, setResults] = useState<{
    total: number;
    migrated: number;
    skipped: number;
    failed: number;
  }>({
    total: 0,
    migrated: 0,
    skipped: 0,
    failed: 0,
  });
  const [log, setLog] = useState<string[]>([]);

  const addLogEntry = (message: string) => {
    setLog((prev) => [...prev, `[${new Date().toLocaleTimeString()}] ${message}`]);
  };

  const migrateData = async () => {
    setIsRunning(true);
    setStatus('running');
    setProgress(0);
    setResults({
      total: 0,
      migrated: 0,
      skipped: 0,
      failed: 0,
    });
    setLog([]);

    try {
      // First, get all solutions
      addLogEntry('Fetching solutions...');
      
      const { data: solutions, error } = await supabase
        .from('solutions')
        .select(`
          id, 
          name, 
          short_description, 
          details,
          solution_categories(categories(name))
        `);
        
      if (error) throw error;
      
      if (!solutions || solutions.length === 0) {
        addLogEntry('No solutions found to migrate.');
        setStatus('completed');
        setIsRunning(false);
        return;
      }
      
      addLogEntry(`Found ${solutions.length} solutions total.`);
      setResults(prev => ({ ...prev, total: solutions.length }));
      
      // Process each solution
      for (let i = 0; i < solutions.length; i++) {
        const solution = solutions[i];
        setProgress(Math.floor((i / solutions.length) * 100));
        
        // Skip if details already exists and is an object
        if (solution.details && typeof solution.details === 'object' && !Array.isArray(solution.details)) {
          addLogEntry(`Skipping ${solution.name}: Already has details.`);
          setResults(prev => ({ ...prev, skipped: prev.skipped + 1 }));
          continue;
        }
        
        try {
          // Get the category name
          const category = solution.solution_categories?.[0]?.categories?.name;
          
          // Generate details for this solution
          const details = generateSolutionDetails(solution.name, solution.short_description, category);
          
          // Convert SolutionDetails to JSON-compatible object
          const jsonDetails = {
            overview: details.overview,
            capabilities: details.capabilities,
            useCases: details.useCases,
            limitations: details.limitations,
            ...(details.longDescription ? { longDescription: details.longDescription } : {})
          };
          
          // Update the solution
          const { error: updateError } = await supabase
            .from('solutions')
            .update({ details: jsonDetails })
            .eq('id', solution.id);
            
          if (updateError) throw updateError;
          
          addLogEntry(`Migrated ${solution.name} successfully.`);
          setResults(prev => ({ ...prev, migrated: prev.migrated + 1 }));
        } catch (err) {
          console.error(`Error migrating solution ${solution.name}:`, err);
          addLogEntry(`Failed to migrate ${solution.name}: ${(err as Error).message}`);
          setResults(prev => ({ ...prev, failed: prev.failed + 1 }));
        }
      }
      
      setProgress(100);
      setStatus('completed');
      addLogEntry('Migration completed.');
      
      toast({
        title: "Migration Completed",
        description: `Migrated ${results.migrated} solutions. Skipped ${results.skipped}. Failed ${results.failed}.`,
      });
    } catch (error) {
      console.error('Migration error:', error);
      addLogEntry(`Migration failed: ${(error as Error).message}`);
      setStatus('error');
      
      toast({
        title: "Migration Failed",
        description: `An error occurred: ${(error as Error).message}`,
        variant: "destructive",
      });
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Migrate Solution Details</CardTitle>
        <CardDescription>
          Populate the 'details' field for solutions that don't have it yet. This will create
          structured data for each solution, making them easier to edit and maintain.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {status === 'idle' && (
          <Alert>
            <InfoIcon className="h-4 w-4" />
            <AlertTitle>Information</AlertTitle>
            <AlertDescription>
              This tool will generate detailed information for all solutions that don't
              already have structured details. It will not overwrite existing data.
            </AlertDescription>
          </Alert>
        )}
        
        {status === 'running' && (
          <div className="space-y-4">
            <Progress value={progress} className="h-2" />
            <p className="text-sm text-muted-foreground">Processing solutions... {progress}% complete</p>
          </div>
        )}
        
        {status === 'completed' && (
          <Alert variant="default" className="border-green-200 bg-green-50 dark:bg-green-900/10">
            <CheckCircleIcon className="h-4 w-4 text-green-600 dark:text-green-400" />
            <AlertTitle>Migration Completed</AlertTitle>
            <AlertDescription>
              <div className="mt-2 space-y-2">
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-500">{results.migrated}</Badge>
                  <span>Solutions migrated successfully</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{results.skipped}</Badge>
                  <span>Solutions skipped (already had details)</span>
                </div>
                {results.failed > 0 && (
                  <div className="flex items-center gap-2">
                    <Badge variant="destructive">{results.failed}</Badge>
                    <span>Solutions failed to migrate</span>
                  </div>
                )}
              </div>
            </AlertDescription>
          </Alert>
        )}
        
        {status === 'error' && (
          <Alert variant="destructive">
            <AlertTriangleIcon className="h-4 w-4" />
            <AlertTitle>Migration Failed</AlertTitle>
            <AlertDescription>
              There was an error during the migration process. Check the log for details.
            </AlertDescription>
          </Alert>
        )}
        
        {(status === 'completed' || status === 'error') && log.length > 0 && (
          <div className="mt-4">
            <h4 className="text-sm font-medium mb-2">Migration Log</h4>
            <div className="bg-muted/50 rounded-md p-2 h-48 overflow-y-auto font-mono text-xs">
              {log.map((entry, index) => (
                <div key={index} className="py-0.5">{entry}</div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
      <CardFooter>
        <Button 
          onClick={migrateData} 
          disabled={isRunning}
          className="w-full"
        >
          {isRunning ? 'Migrating...' : 'Start Migration'}
        </Button>
      </CardFooter>
      <Separator />
      <div className="p-4 bg-muted/50 text-xs text-muted-foreground">
        <p>This action only affects solutions without a structured details object.</p>
      </div>
    </Card>
  );
};

export default MigrateSolutionData;
